import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { DriverGroupDto } from '@cargovibe/shared-types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { useCreateDriverGroupForm } from '@/modules/driver-groups/hooks/useCreateDriverGroupForm';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (group: DriverGroupDto) => void;
}

export function CreateDriverGroupDialog({
  isOpen,
  onClose,
  onCreated,
}: Readonly<Props>): React.JSX.Element {
  const { t } = useTranslation();
  const {
    name,
    errorMessage,
    isPending,
    handleNameChange,
    handleSave,
  }: ReturnType<typeof useCreateDriverGroupForm> = useCreateDriverGroupForm(onCreated);

  const handleOpenChange = (open: boolean): void => {
    if (!open) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('driverGroups.createGroup')}</DialogTitle>
          <DialogDescription>{t('driverGroups.createGroupSub')}</DialogDescription>
        </DialogHeader>

        <label className="flex flex-col gap-1.5 text-sm font-medium text-foreground">
          {t('driverGroups.groupName')}
          <Input
            value={name}
            onChange={handleNameChange}
            placeholder={t('driverGroups.groupNamePlaceholder')}
          />
        </label>

        {errorMessage && (
          <p role="alert" className="text-sm text-destructive">
            {errorMessage}
          </p>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('driverGroups.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
